/**
 * Session Monitor Component
 *
 * Monitors Clerk session state and handles session expiration gracefully:
 * - Actively checks session validity every 5 minutes
 * - Shows warning modal when session expires
 * - Preserves current URL for redirect after re-login
 * - Provides clear user feedback
 *
 * IMPORTANT: This component must be rendered inside ClerkProvider
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuth, useClerk, useSession } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

export default function SessionMonitor() {
  const { isSignedIn, isLoaded } = useAuth();
  const { session } = useSession();
  const clerk = useClerk();
  const location = useLocation();
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [wasSignedIn, setWasSignedIn] = useState(false);

  // Check if session is valid and active
  const checkSessionValidity = useCallback(async () => {
    if (!isSignedIn || !session) {
      return;
    }

    try {
      // Touch the session to trigger auto-refresh
      // Clerk automatically refreshes the token when accessed
      await session.touch();
    } catch (error) {
      console.error('Session check failed:', error);
      // Session is invalid, show expiration modal
      const protectedRoutes = ['/chat', '/dashboard', '/settings', '/profile', '/subscription', '/referrals'];
      const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));

      if (isProtectedRoute) {
        setShowExpiredModal(true);
      }
    }
  }, [isSignedIn, session, location.pathname]);

  // Active session monitoring - check every 5 minutes
  useEffect(() => {
    if (!isSignedIn) {
      return;
    }

    // Initial check
    checkSessionValidity();

    // Check every 5 minutes during active use
    const interval = setInterval(checkSessionValidity, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isSignedIn, checkSessionValidity]);

  // Passive session expiration detection
  useEffect(() => {
    // Track when user becomes signed in
    if (isLoaded && isSignedIn) {
      setWasSignedIn(true);
    }

    // Detect session expiration: was signed in, now is not
    if (isLoaded && !isSignedIn && wasSignedIn) {
      // Only show modal if we're on a protected route
      const protectedRoutes = ['/chat', '/dashboard', '/settings', '/profile', '/subscription', '/referrals'];
      const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));

      if (isProtectedRoute) {
        setShowExpiredModal(true);
      }

      // Reset tracking
      setWasSignedIn(false);
    }
  }, [isLoaded, isSignedIn, wasSignedIn, location.pathname]);

  const handleRelogin = () => {
    // Store return URL in session storage for redirect after login
    sessionStorage.setItem('clerk_return_url', location.pathname + location.search);

    // Close modal
    setShowExpiredModal(false);

    // Navigate to sign in with return URL
    clerk.redirectToSignIn({
      redirectUrl: location.pathname + location.search,
    });
  };

  const handleDismiss = () => {
    setShowExpiredModal(false);
    // User will be redirected by ProtectedRoute
  };

  if (!showExpiredModal) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Session Expired
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Your session has expired for security reasons. Please sign in again to continue.
            </p>
            <p className="text-xs text-gray-500 mb-6">
              Don't worry - we'll bring you right back to where you were.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleRelogin}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium min-h-[44px]"
              >
                Sign In Again
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
